import { useEffect, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui';
import { SupportPill } from '@/components/SupportPill';
import { t } from '@/lib/i18n';
import { LanguageSwitch } from '@/components/LanguageSwitch';

type Mode = 'password' | 'magic' | 'forgot' | 'register';

export function LoginPage() {
  const [mode, setMode] = useState<Mode>('password');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set when the server accepted the password but wants the emailed code.
  const [challenge, setChallenge] = useState<{ handle: string; hint: string | null } | null>(null);
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(true);
  // "Sign in with Microsoft" — only offered when the server has it configured.
  const [ssoEnabled, setSsoEnabled] = useState(false);
  useEffect(() => {
    fetch('/api/auth/saml/enabled')
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { enabled?: boolean } | null) => setSsoEnabled(Boolean(j?.enabled)))
      .catch(() => {});
    // A failed Microsoft sign-in comes back here as ?sso=<reason>.
    const why = new URLSearchParams(window.location.search).get('sso');
    if (why) setError(t(why === 'noaccount' ? 'loginPage.ssoNoAccount' : 'loginPage.ssoFailed'));
  }, []);

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.login(username, password);
      if (res.challenge) {
        setChallenge(res.challenge);
        setBusy(false);
        return;
      }
      window.location.assign('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('loginPage.connectionFailed'));
      setBusy(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      await api.submitLoginCode(challenge.handle, code.trim(), remember);
      window.location.assign('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('loginPage.connectionFailed'));
      setBusy(false);
      // An expired or burnt-out challenge cannot be retried — send them back to
      // the password form rather than leaving them typing into a dead code box.
      if (err instanceof ApiError && /expired|Too many/i.test(err.raw)) {
        setChallenge(null);
        setCode('');
      }
    }
  }

  async function submitForgot(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.forgotPassword(email);
      // Same wording whether or not the address exists — the server does not
      // say, and neither should we.
      setNotice(t('loginPage.resetLinkSent'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('loginPage.connectionFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function submitRegister(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.register(email, fullName);
      setNotice(t('loginPage.registerSent'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('loginPage.connectionFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function submitMagic(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    setDevLink(null);
    try {
      const res = await api.requestMagicLink(email);
      setNotice(t('loginPage.loginLinkSent'));
      if (res.devLink) setDevLink(res.devLink); // no mail key configured — show the link directly
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('loginPage.connectionFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-white px-4">
      <div className="fixed right-4 top-4 z-10">
        <LanguageSwitch tone="light" />
      </div>
      <div className="w-full max-w-[400px]">
        <div className="rounded-2xl bg-white p-10 text-gray-900 shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
          <div className="mb-6 flex flex-col items-center gap-1">
            <img src="/aicotone-logo.png" alt="AIcotone" className="h-12 w-auto" />
            <span className="text-xs font-medium uppercase tracking-widest text-gray-400">{t('loginPage.subtitle')}</span>
          </div>

          {challenge ? (
            <form onSubmit={submitCode} className="space-y-4">
              <p className="text-[13px] text-gray-600">
                {t('loginPage.sentCodeTo')}{' '}
                <span className="font-medium text-gray-900">{challenge.hint ?? t('loginPage.yourEmailAddress')}</span>{t('loginPage.codeSentSuffix')}
              </p>
              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-gray-700">{t('loginPage.signInCode')}</span>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-center text-lg tracking-[0.4em] text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </label>
              <label className="flex items-center gap-2 text-[13px] text-gray-700">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
                {t('loginPage.rememberDevice')}
              </label>
              {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700" role="alert">{error}</p>}
              <Button type="submit" variant="primary" loading={busy} className="w-full">{t('loginPage.verifyAndSignIn')}</Button>
              <button
                type="button"
                onClick={() => { setChallenge(null); setCode(''); setError(null); }}
                className="w-full text-center text-[13px] font-medium text-brand-600 hover:text-brand-700"
              >
                {t('loginPage.backToSignIn')}
              </button>
            </form>
          ) : mode === 'password' ? (
            <form onSubmit={submitPassword} className="space-y-4">
              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-gray-700">{t('loginPage.username')}</span>
                <input value={username} onChange={(e) => setUsername(e.target.value)} required autoFocus autoComplete="username"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </label>
              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-gray-700">{t('loginPage.password')}</span>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </label>
              {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700" role="alert">{error}</p>}
              <Button type="submit" variant="primary" loading={busy} className="w-full">{t('loginPage.signIn')}</Button>
              <button
                type="button"
                onClick={() => { setMode('forgot'); setError(null); setNotice(null); }}
                className="w-full text-center text-[13px] text-gray-500 hover:text-gray-700"
              >
                {t('loginPage.forgotPassword')}
              </button>
            </form>
          ) : mode === 'register' ? (
            <form onSubmit={submitRegister} className="space-y-4">
              <p className="text-[13px] text-gray-600">{t('loginPage.registerIntro')}</p>
              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-gray-700">{t('loginPage.fullName')}</span>
                <input value={fullName} onChange={(e) => setFullName(e.target.value)} required autoFocus autoComplete="name"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </label>
              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-gray-700">{t('loginPage.workEmail')}</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                  placeholder={t('loginPage.emailPlaceholder')}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </label>
              {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700" role="alert">{error}</p>}
              {notice && <p className="rounded-lg bg-green-50 px-3 py-2 text-[13px] text-green-800" role="status">{notice}</p>}
              <Button type="submit" variant="primary" loading={busy} className="w-full">{t('loginPage.registerButton')}</Button>
              <button
                type="button"
                onClick={() => { setMode('password'); setError(null); setNotice(null); }}
                className="w-full text-center text-[13px] font-medium text-brand-600 hover:text-brand-700"
              >
                {t('loginPage.backToSignIn')}
              </button>
            </form>
          ) : mode === 'forgot' ? (
            <form onSubmit={submitForgot} className="space-y-4">
              <p className="text-[13px] text-gray-600">
                {t('loginPage.forgotIntro')}
              </p>
              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-gray-700">{t('loginPage.emailAddress')}</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus autoComplete="email"
                  placeholder={t('loginPage.emailPlaceholder')}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </label>
              {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700" role="alert">{error}</p>}
              {notice && <p className="rounded-lg bg-green-50 px-3 py-2 text-[13px] text-green-800" role="status">{notice}</p>}
              <Button type="submit" variant="primary" loading={busy} className="w-full">{t('loginPage.sendResetLink')}</Button>
              <button
                type="button"
                onClick={() => { setMode('password'); setError(null); setNotice(null); }}
                className="w-full text-center text-[13px] font-medium text-brand-600 hover:text-brand-700"
              >
                {t('loginPage.backToSignIn')}
              </button>
            </form>
          ) : (
            <form onSubmit={submitMagic} className="space-y-4">
              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-gray-700">{t('loginPage.emailAddress')}</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus autoComplete="email"
                  placeholder={t('loginPage.emailPlaceholder')}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </label>
              {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700" role="alert">{error}</p>}
              {notice && <p className="rounded-lg bg-green-50 px-3 py-2 text-[13px] text-green-800" role="status">{notice}</p>}
              {devLink && (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-800 break-all">
                  {t('loginPage.devLinkNotice')}{' '}
                  <a href={devLink} className="font-medium underline">{devLink}</a>
                </p>
              )}
              <Button type="submit" variant="primary" loading={busy} className="w-full">{t('loginPage.sendLoginLink')}</Button>
            </form>
          )}

          {/* Mid-challenge the only sensible moves are "verify" or "back", both
              already in that form — offering a mode switch here would silently
              abandon the pending code. */}
          {/* 'forgot' has its own "back to sign in" control, so the toggle would
              be a third, contradictory way out of the same form. */}
          {!challenge && mode !== 'forgot' && mode !== 'register' && (
            <button
              type="button"
              onClick={() => { setMode(mode === 'password' ? 'magic' : 'password'); setError(null); setNotice(null); setDevLink(null); }}
              className="mt-4 w-full text-center text-[13px] font-medium text-brand-600 hover:text-brand-700"
            >
              {mode === 'password' ? t('loginPage.signInWithEmailLink') : t('loginPage.signInWithPassword')}
            </button>
          )}
          {!challenge && mode !== 'forgot' && mode !== 'register' && ssoEnabled && (
            <>
              <div className="my-4 flex items-center gap-3 text-[12px] text-gray-400">
                <span className="h-px flex-1 bg-gray-200" />
                {t('loginPage.or')}
                <span className="h-px flex-1 bg-gray-200" />
              </div>
              <a
                href="/api/auth/saml/login"
                className="flex w-full items-center justify-center gap-2.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                <svg viewBox="0 0 21 21" className="size-4" aria-hidden="true">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
                {t('loginPage.signInWithMicrosoft')}
              </a>
            </>
          )}
          {!challenge && mode !== 'register' && mode !== 'forgot' && (
            <p className="mt-5 text-center text-[13px] text-gray-500">
              {t('loginPage.noAccount')}{' '}
              <button
                type="button"
                onClick={() => { setMode('register'); setError(null); setNotice(null); }}
                className="font-medium text-brand-600 hover:text-brand-700"
              >
                {t('loginPage.createAccount')}
              </button>
            </p>
          )}
        </div>
      </div>
      {/* Someone locked out has no other route to us, so the pill is on this page too. */}
      <SupportPill signedOut />
    </div>
  );
}
